/**
 * Photo URL Utilities
 * 
 * Builds proxy photo URLs that route through the getGymPhoto Cloud Function.
 * Photos are fetched fresh from Google Places API (ToS compliant) and CDN-cached for 24h.
 */

import { DEFAULT_STATE_DISPLAY } from './stateRoutes';

const FUNCTIONS_REGION = 'australia-southeast1';
const PROJECT_ID = 'martial-arts-system';
const FN_ORIGIN = `https://${FUNCTIONS_REGION}-${PROJECT_ID}.cloudfunctions.net`;

/**
 * Photo proxy base URLs.
 *
 * In PRODUCTION these are SAME-ORIGIN paths (`/photo`, `/photo-meta`) that
 * firebase.json rewrites to the getGymPhoto / getGymPhotoMeta functions THROUGH
 * Firebase Hosting's Fastly CDN — so responses edge-cache across users (Phase 1
 * item 10) instead of hitting the cloudfunctions.net origin per visitor. Covered
 * by CSP `img-src 'self'` / `connect-src 'self'`.
 *
 * In local DEV there is no Hosting rewrite, so hit the function origin directly.
 */
const PHOTO_PROXY_BASE = import.meta.env.DEV ? `${FN_ORIGIN}/getGymPhoto` : `/photo`;
const PHOTO_META_BASE = import.meta.env.DEV ? `${FN_ORIGIN}/getGymPhotoMeta` : `/photo-meta`;

/**
 * Canonical width allowlist. Every content photo URL snaps to one of these,
 * bounding the CDN/cold-store key space to 4 widths per (placeId,index) instead
 * of the unbounded per-pixel ladder that drove the Place Photos SKU. MUST stay
 * in lock-step with the server-side snap in functions/src/index.ts (getGymPhoto).
 * See docs/audit/photo-cost-redesign.md.
 */
export const CANONICAL_WIDTHS = [400, 800, 1200, 1600] as const;
const CANONICAL_MAXHEIGHT = 4800; // width is always the binding constraint

/** Snap any requested width UP to the nearest canonical width (never smaller). */
function snapWidth(w: number): number {
    for (const c of CANONICAL_WIDTHS) if (w <= c) return c;
    return CANONICAL_WIDTHS[CANONICAL_WIDTHS.length - 1]!;
}

/**
 * Build a proxy photo URL for a gym.
 * 
 * @param placeId - Google Place ID of the gym
 * @param index - Photo index (0 = primary, 1-9 for gallery)
 * @param width - Requested width in px; snapped to CANONICAL_WIDTHS (height is fixed at the proxy ceiling so width binds). Use getGymOgImageUrl for the 1200×630 social crop.
 */
function getGymPhotoUrl(placeId: string, index: number = 0, width: number = 800): string {
    const params = new URLSearchParams({
        placeId,
        index: index.toString(),
        maxWidth: snapWidth(width).toString(),
        maxHeight: CANONICAL_MAXHEIGHT.toString(),
    });
    return `${PHOTO_PROXY_BASE}?${params.toString()}`;
}

/**
 * Social-share (og:image) URL — a single fixed 1200×630 crop the server serves
 * un-snapped. Kept separate so the OG crop never forks the canonical width set.
 */
export function getGymOgImageUrl(placeId: string): string {
    const params = new URLSearchParams({
        placeId, index: '0', maxWidth: '1200', maxHeight: '630',
    });
    // og:image MUST be absolute for social scrapers. In dev PHOTO_PROXY_BASE is already the
    // absolute function origin; in prod it is the same-origin '/photo', so prepend the site
    // origin. (The SSG gym branch builds the same absolute URL — keep the two in sync.)
    const base = import.meta.env.DEV ? PHOTO_PROXY_BASE : `https://martialartnetwork.com.au${PHOTO_PROXY_BASE}`;
    return `${base}?${params.toString()}`;
}

/**
 * Build a thumbnail URL (card-sized, smaller for faster load).
 */
export function getGymThumbnailUrl(placeId: string): string {
    return getGymPhotoUrl(placeId, 0, 400);
}

/**
 * Build gallery photo URLs for the gym profile page (up to 3 photos).
 */
export function getGymGalleryUrls(placeId: string, count: number = 3): string[] {
    return Array.from({ length: count }, (_, i) => getGymPhotoUrl(placeId, i, 800));
}

/**
 * Build a responsive `srcset` string for one gym photo by varying the width.
 *
 * Returns "url 300w, url 600w, ..." — pure string, no side effects. Pair it
 * with a per-surface `sizes` attribute; the browser then picks the smallest
 * candidate that satisfies the displayed CSS width × devicePixelRatio (so DPR
 * is handled automatically — no separate 1x/2x set needed).
 *
 * Width-only: each candidate requests `maxHeight = 4800` (the proxy's clamp
 * ceiling) so the WIDTH is always the binding constraint and Google returns an
 * image ~`w` px wide with its aspect preserved. This keeps the `Nw` descriptors
 * honest across landscape/portrait photos without per-width height math.
 */
export function getGymPhotoSrcSet(placeId: string, index: number): string {
    return CANONICAL_WIDTHS
        .map(w => `${getGymPhotoUrl(placeId, index, w)} ${w}w`)
        .join(', ');
}

/**
 * A Google Places photo author attribution. `displayName` is the contributor;
 * `uri` links to their profile (both required for the policy credit when present).
 */
export interface PhotoAttribution {
    displayName: string;
    uri?: string | null;
    photoUri?: string | null;
}

/**
 * Fetch per-photo author attributions for a gym.
 *
 * Google Places policy: when a photo carries authorAttributions, the credit
 * must be shown near the image. This hits the getGymPhotoMeta JSON endpoint
 * (fresh-fetch, no storage) and returns a map of gallery photo index →
 * attributions. Index N here matches index N from getGymGalleryUrls /
 * getGymPhotoUrl, so callers can line attributions up with displayed photos.
 *
 * Only indices that actually have attributions are included in the map (photos
 * without attribution are omitted), so an empty map means "no credit needed".
 * Throws on network/HTTP error — callers should treat failure as "no credit".
 */
/**
 * Per-gym photo metadata: author attributions AND Google's authoritative photo
 * dimensions, both keyed by gallery photo index (matching getGymGalleryUrls /
 * getGymPhotoUrl). Dimensions let the profile size/sort the gallery without a
 * separate per-photo probe fetch. Only indices that actually carry each field
 * are included (so an empty map means "none").
 */
interface GymPhotoMeta {
    attributions: Record<number, PhotoAttribution[]>;
    dimensions: Record<number, { width: number; height: number }>;
}

export async function getGymPhotoMeta(placeId: string): Promise<GymPhotoMeta> {
    const res = await fetch(`${PHOTO_META_BASE}?placeId=${encodeURIComponent(placeId)}`);
    if (!res.ok) throw new Error(`getGymPhotoMeta ${res.status}`);
    const data = await res.json() as {
        photos?: Array<{
            index: number;
            width?: number | null;
            height?: number | null;
            authorAttributions?: PhotoAttribution[];
        }>;
    };
    const attributions: Record<number, PhotoAttribution[]> = {};
    const dimensions: Record<number, { width: number; height: number }> = {};
    for (const p of data.photos || []) {
        if (!p || typeof p.index !== 'number') continue;
        if (Array.isArray(p.authorAttributions) && p.authorAttributions.length > 0) {
            const attrs = p.authorAttributions.filter(a => a && a.displayName);
            if (attrs.length > 0) attributions[p.index] = attrs;
        }
        if (typeof p.width === 'number' && typeof p.height === 'number' && p.width > 0 && p.height > 0) {
            dimensions[p.index] = { width: p.width, height: p.height };
        }
    }
    return { attributions, dimensions };
}

/**
 * Build accessible, SEO-useful alt text for a gym photo from OWNED gym metadata
 * only (name, first style, suburb) — never Google Places photo content.
 *
 * Pattern (name-first), with graceful degradation so we never emit
 * "undefined"/"null" or dangling punctuation:
 *   - style + suburb: "{name} — {style} martial arts gym in {suburb}, NSW"
 *   - no style:       "{name} — martial arts gym in {suburb}, NSW"
 *   - no suburb:      "{name} — martial arts gym"
 *   - no name (edge): "Martial arts gym in {suburb}, NSW" / "Martial arts gym"
 *
 * Returns the plain base string; the lightbox caller appends ", enlarged view".
 * Accepts a structural subset of Gym so it stays decoupled from the full type.
 */
export function buildGymAltText(gym: {
    name?: string | null;
    allStyles?: string[] | null;
    suburb?: string | null;
    /** Display form, e.g. 'NSW'. Absent (pre-R3 display docs) resolves to the default state. */
    state?: string | null;
}): string {
    const name = gym.name?.trim();
    const style = gym.allStyles?.[0]?.trim();
    const suburb = gym.suburb?.trim();
    const stateDisplay = gym.state?.trim() || DEFAULT_STATE_DISPLAY;

    // "{style} martial arts gym" or just "martial arts gym"
    const descriptor = style ? `${style} martial arts gym` : 'martial arts gym';
    const location = suburb ? ` in ${suburb}, ${stateDisplay}` : '';

    if (name) {
        return `${name} — ${descriptor}${location}`;
    }
    // No-name edge: capitalise the descriptor as the sentence start.
    const capitalised = descriptor.charAt(0).toUpperCase() + descriptor.slice(1);
    return `${capitalised}${location}`;
}
